import * as React from 'react';
import { route } from '../../utils/route';

interface Plan {
  id: number;
  name: string;
  slug: string;
  price: number;
  badge: string | null;
  description: string | null;
  features: string[];
}

interface PlanComparisonProps {
  plans: Plan[];
  /** key -> label, in catalog order; used to render a consistent ✓/✗ matrix */
  featureCatalog: Record<string, string>;
}

const formatPrice = (amount: number) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
  }).format(amount);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-5 w-5 shrink-0 text-[#1EAA25]">
    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5 shrink-0 text-gray-300">
    <path d="M5 12h14" strokeLinecap="round" />
  </svg>
);

export function PlanComparison({ plans, featureCatalog }: PlanComparisonProps) {
  const catalogKeys = Object.keys(featureCatalog);

  return (
    <section className="mx-auto max-w-5xl px-6 py-16">
      <div className="mb-10 text-center">
        <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">Elegí tu plan</h2>
        <p className="mt-2 text-gray-500">Cancelás cuando quieras. Sin permanencia.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {plans.map(plan => {
          const included = new Set(plan.features);
          const highlighted = Boolean(plan.badge);
          return (
            <div
              key={plan.id}
              className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition ${
                highlighted ? 'border-[#1EAA25] ring-2 ring-[#1EAA25]/20' : 'border-gray-200'
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#1EAA25] px-3 py-1 text-xs font-semibold text-white">
                  {plan.badge}
                </span>
              )}

              <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
              <p className="mt-1 min-h-[40px] text-sm text-gray-500">{plan.description}</p>

              <div className="mt-4">
                <span className="text-3xl font-extrabold text-gray-900">{formatPrice(plan.price)}</span>
                <span className="text-base font-normal text-gray-500">/mes</span>
              </div>

              <ul className="mt-6 flex-1 space-y-3">
                {catalogKeys.map(key => {
                  const has = included.has(key);
                  return (
                    <li key={key} className={`flex items-start gap-2 text-sm ${has ? 'text-gray-700' : 'text-gray-400'}`}>
                      {has ? <CheckIcon /> : <DashIcon />}
                      <span>{featureCatalog[key]}</span>
                    </li>
                  );
                })}
              </ul>

              <a
                href={route('subscription.show', { slug: plan.slug })}
                className={`mt-6 block rounded-xl px-6 py-3 text-center text-base font-semibold transition ${
                  highlighted
                    ? 'bg-[#1EAA25] text-white hover:bg-[#178f1e]'
                    : 'border border-[#1EAA25] text-[#1EAA25] hover:bg-[#1EAA25]/5'
                }`}
              >
                Elegir {plan.name}
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
}
